/**
 * 敏感词 Trie 前缀树引擎
 *
 * 核心优势：搜索时间复杂度 O(text_len × max_word_len)，与词库大小几乎无关。
 * 即使词库从 2 万条增长到 20 万条，搜索速度不变。
 *
 * 匹配规则（与原有行为兼容）：
 * 1. 大小写不敏感匹配（统一转小写索引，提高命中率）
 * 2. 纯英文词（/^\w+$/）需要单词边界检查，避免 "Iran" 误匹配 "Iranian"
 * 3. 含符号/空格的词直接子串匹配，不做边界检查
 */

/**
 * 创建一个新的 Trie 节点
 * 使用 Object.create(null) 避免原型链属性干扰
 */
function createNode() {
    return {
        children: null,     // 懒创建：有子节点时才分配对象
        isEnd: false,       // 是否是词尾节点
        word: null,         // isEnd=true 时存储原始词（保留原始大小写）
        isPureWord: false   // 是否是纯英文词（需要单词边界检查）
    }
}

class SensitiveWordTrie {
    constructor() {
        this.root = createNode()
        this._wordCount = 0
    }

    /**
     * 批量构建 Trie
     * @param {string[]} words - 敏感词数组
     */
    build(words) {
        // 清空旧数据
        this.root = createNode()
        this._wordCount = 0

        for (const word of words) {
            if (!word || typeof word !== 'string') continue
            const trimmed = word.trim()
            if (!trimmed) continue
            this._insert(trimmed)
        }
    }

    /**
     * 插入单个词到 Trie
     * @param {string} word - 原始词
     */
    _insert(word) {
        let node = this.root
        const isPureWord = /^\w+$/.test(word)

        for (let i = 0; i < word.length; i++) {
            const char = word[i]
            const lower = char.toLowerCase()

            // 主路径：原始大小写
            if (!node.children) {
                node.children = Object.create(null)
            }
            if (!node.children[char]) {
                node.children[char] = createNode()
            }
            // 同时建立小写路径（如果不同），实现大小写不敏感
            if (lower !== char) {
                if (!node.children[lower]) {
                    node.children[lower] = createNode()
                }
            }

            node = node.children[char]
        }

        node.isEnd = true
        node.word = word
        node.isPureWord = isPureWord

        // 如果原始词有大写字符，同时在全小写路径上标记
        if (word !== word.toLowerCase()) {
            let lowerNode = this.root
            for (let i = 0; i < word.length; i++) {
                const lower = word[i].toLowerCase()
                if (!lowerNode.children) {
                    lowerNode.children = Object.create(null)
                }
                if (!lowerNode.children[lower]) {
                    lowerNode.children[lower] = createNode()
                }
                lowerNode = lowerNode.children[lower]
            }
            lowerNode.isEnd = true
            lowerNode.word = word
            lowerNode.isPureWord = isPureWord
        }

        this._wordCount++
    }

    /**
     * 在文本中搜索所有匹配的敏感词
     *
     * 算法：从文本的每个位置 i 开始，沿 Trie 向下走，
     * 命中词尾节点时进行边界检查，通过则加入结果集。
     *
     * @param {string} text - 待检测文本
     * @param {number} [maxResults=20] - 最多返回的匹配数
     * @returns {string[]} 匹配到的敏感词（原始大小写形式）
     */
    search(text, maxResults = 20) {
        if (!text || typeof text !== 'string') return []

        const matched = new Set()
        const len = text.length

        for (let i = 0; i < len; i++) {
            let node = this.root

            for (let j = i; j < len; j++) {
                const char = text[j]

                // 尝试原始字符和小写字符路径
                if (!node.children) break
                let nextNode = node.children[char]
                if (!nextNode && char !== char.toLowerCase()) {
                    nextNode = node.children[char.toLowerCase()]
                }
                if (!nextNode) break

                node = nextNode

                if (node.isEnd) {
                    // 命中词尾 text[i..j]，进行边界检查
                    let valid = true
                    if (node.isPureWord) {
                        // 左边界：前一个字符不能是单词字符
                        if (i > 0 && /^\w$/.test(text[i - 1])) valid = false
                        // 右边界：后一个字符不能是单词字符
                        if (valid && j + 1 < len && /^\w$/.test(text[j + 1])) valid = false
                    }

                    if (valid) {
                        matched.add(node.word)
                    }
                    // 不 break：可能存在更长的匹配
                }

                // 提前退出：已收集到足够结果
                if (matched.size >= maxResults) break
            }

            if (matched.size >= maxResults) break
        }

        return Array.from(matched)
    }

    /**
     * 获取已加载的词数
     * @returns {number}
     */
    get wordCount() {
        return this._wordCount
    }
}

export default SensitiveWordTrie
